<template>
    <v-dialog v-model="value" max-width="800">

        <v-card>

            <toolbar-page :title="!client.id ? 'Create new client' : 'Edit client'" icon="mdi-pencil">
                <template slot="buttons">
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <v-btn @click="close()"
                                   v-on="on"
                                   icon>
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close this dialog</span>
                    </v-tooltip>
                </template>
            </toolbar-page>

            <v-card-text class="pb-0">

                <validation-observer ref="validationObserver" v-slot="{ invalid }">

                    <v-container fluid>
                        <v-row>

                            <v-col cols="12" v-if="!hasPermission('update clients')">
                                <v-alert icon="mdi-alert" type="warning" class="body-2">
                                    You don't have permissions to edit clients so we just let you view the client!
                                </v-alert>
                            </v-col>

                            <v-col cols="12">

                                <title-card title="Client info and notes">

                                    <validation-provider
                                            name="Client name"
                                            rules="min:3|max:50|required"
                                            v-slot="{ errors }">

                                        <v-text-field
                                                v-model="data.name"
                                                single-line
                                                counter="50"
                                                lazy
                                                hint="Client name"
                                                persistent-hint
                                                :error-messages="errors"
                                                prepend-icon="mdi-domain"
                                        ></v-text-field>
                                    </validation-provider>

                                    <validation-provider
                                            name="Notes"
                                            rules="min:3|max:10000"
                                            v-slot="{ errors }">

                                        <v-textarea
                                                v-model="data.notes"
                                                rows="1"
                                                hint="Notes"
                                                :error-messages="errors"
                                                persistent-hint
                                                prepend-icon="mdi-message-bulleted"
                                        ></v-textarea>

                                    </validation-provider>
                                </title-card>
                            </v-col>

                            <v-col cols="12" md="6">

                                <title-card title="Primary contact">

                                    <validation-provider
                                            name="Primary contact name"
                                            rules="max:50|required"
                                            v-slot="{ errors }">

                                        <v-text-field
                                                hint="Name"
                                                v-model="data.primary_contact_name"
                                                single-line
                                                counter="50"
                                                persistent-hint
                                                :error-messages="errors"
                                                prepend-icon="mdi-account"
                                        ></v-text-field>
                                    </validation-provider>

                                    <validation-provider
                                            name="Primary contact email"
                                            rules="required|email"
                                            v-slot="{ errors }">

                                        <v-text-field
                                                hint="Email address"
                                                v-model="data.primary_contact_email"
                                                single-line
                                                counter="50"
                                                persistent-hint
                                                :error-messages="errors"
                                                prepend-icon="mdi-at"
                                        ></v-text-field>
                                    </validation-provider>

                                    <v-text-field
                                            hint="Phone number"
                                            v-model="data.primary_contact_phone"
                                            single-line
                                            counter="50"
                                            persistent-hint
                                            prepend-icon="mdi-phone"
                                    ></v-text-field>

                                </title-card>
                            </v-col>

                            <v-col cols="12" md="6">

                                <title-card title="Secondary contact">

                                    <validation-provider
                                            name="Secondary contact name"
                                            rules="max:50"
                                            v-slot="{ errors }">

                                        <v-text-field
                                                hint="Name"
                                                v-model="data.secondary_contact_name"
                                                single-line
                                                counter="50"
                                                persistent-hint
                                                :error-messages="errors"
                                                prepend-icon="mdi-account"
                                        ></v-text-field>
                                    </validation-provider>

                                    <validation-provider
                                            name="Secondary contact email"
                                            rules="email"
                                            v-slot="{ errors }">

                                        <v-text-field
                                                hint="Email address"
                                                v-model="data.secondary_contact_email"
                                                single-line
                                                counter="50"
                                                persistent-hint
                                                :error-messages="errors"
                                                prepend-icon="mdi-at"
                                        ></v-text-field>
                                    </validation-provider>

                                    <v-text-field
                                            hint="Secondary phone number"
                                            v-model="data.secondary_contact_phone"
                                            single-line
                                            counter="50"
                                            persistent-hint
                                            prepend-icon="mdi-phone"
                                    ></v-text-field>

                                </title-card>

                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-btn v-if="hasPermission('update clients')"
                                       color="primary"
                                       @click="saveClicked"
                                       :loading="saving"
                                >
                                    {{!data.id ? 'Create' : 'Update'}}
                                </v-btn>
                                <v-btn
                                        :color="hasPermission('update clients') ? 'secondary' : 'primary'"
                                        @click="cancelClicked"
                                >
                                    Cancel
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-container>
                </validation-observer>
            </v-card-text>
        </v-card>

    </v-dialog>

</template>

<script>

    import ToolbarPage from '../../../components/UI/ToolbarPage';
    import TitleCard from "../../../components/UI/TitleCard";

    export default {

        components: {
            ToolbarPage,
            TitleCard
        },

        props: {
            value: {
                required: true,
                type: Boolean,
            },
            client: {
                required: true,
                type: Object
            },
            saving: {
                required: true,
                type: Boolean,
            }
        },

        watch: {
            client(newObject) {
                this.data = Object.assign({}, newObject);
            }
        },

        data() {
            return {
                data: {}
            }
        },

        created() {
            this.data = Object.assign({}, this.client);
        },

        methods: {

            close() {
                this.$emit('input', false);
            },

            saveClicked() {
                if (this.$refs.validationObserver) {
                    this.$refs.validationObserver.validate()
                        .then(isValid => {

                            if (isValid) {
                                this.$emit('change', this.data);
                            } else {
                                Bus.$emit('flash-message', {
                                    type: 'error',
                                    message: 'Form contains errors please review!'
                                });
                            }
                        });
                }
            },

            cancelClicked() {
                this.$emit('input', false);
            }
        }
    }
</script>
